// pages/Vehicles.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Truck, 
  MapPin, 
  User, 
  Fuel, 
  Gauge, 
  Wrench, 
  AlertCircle,
  Eye,
  Play,
  RefreshCw,
  Search,
  Filter,
  Calendar,
  Palette,
  Settings,
  Battery,
  ChevronRight,
  MoreVertical,
  Clock,
  Navigation,
  Plus,
  X,
  CheckCircle,
  Activity,
  Thermometer,
  Zap,
  Edit,
  Radio,
  Signal,
  Wifi,
  Target,
  Map,
  Route,
  Shield,
  BatteryCharging,
  Globe,
  AlertTriangle,
  Save,
  Loader2
} from 'lucide-react';
import { 
  vehiclesAPI, 
  driversAPI, 
  testConnection,
  getWebSocketURL 
} from '../services/api'; // Adjust path based on your structure

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showAssignDriverModal, setShowAssignDriverModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({});
  const [trackingVehicles, setTrackingVehicles] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  const [newVehicle, setNewVehicle] = useState({
    registration: '',
    model: '',
    year: new Date().getFullYear(),
    vehicleType: 'truck',
    fuelLevel: 0,
    mileage: 0,
    specifications: {
      engineSize: '',
      fuelType: 'diesel',
      capacity: '',
      color: ''
    }
  });
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [websocket, setWebsocket] = useState(null);
  const [realtimeData, setRealtimeData] = useState({});
  const [connected, setConnected] = useState(false);
  const [showTrackingControls, setShowTrackingControls] = useState({});
  const [updateLoading, setUpdateLoading] = useState({});
  const [error, setError] = useState('');

  const WS_URL = getWebSocketURL();

  // WebSocket reference
  const wsRef = useRef(null);

  // Initialize WebSocket connection
  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Fetch vehicles data using API
  useEffect(() => {
    fetchVehicles();
  }, []);

  // Connect to WebSocket
  const connectWebSocket = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const ws = new WebSocket(`${WS_URL}?token=${token}`);
      
      ws.onopen = () => {
        console.log('WebSocket Connected');
        setConnected(true);
        wsRef.current = ws;
        
        // Subscribe to vehicle updates
        ws.send(JSON.stringify({
          type: 'subscribe',
          channel: 'vehicle_locations'
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          switch(data.type) {
            case 'vehicle_location_update':
              handleVehicleUpdate(data);
              break;
            case 'location_update':
              handleVehicleUpdate(data);
              break;
            case 'vehicle_assigned':
              handleVehicleAssignment(data);
              break;
            case 'vehicle_unassigned':
              handleVehicleUnassignment(data);
              break;
            case 'tracking_started':
              handleTrackingUpdate(data.vehicleId, true);
              break;
            case 'tracking_stopped':
              handleTrackingUpdate(data.vehicleId, false);
              break;
            case 'alert':
              if (data.vehicleId) {
                showAlertNotification(data);
              }
              break;
            case 'assignment_update':
              if (data.action === 'assigned') {
                // Refresh vehicle data when assignment happens
                fetchVehicles();
              }
              break;
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket Error:', error);
        setConnected(false);
      };

      ws.onclose = () => {
        console.log('WebSocket Disconnected');
        setConnected(false);
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
    }
  };

  const handleVehicleUpdate = (data) => {
    setRealtimeData(prev => ({
      ...prev,
      [data.vehicleId]: {
        ...data,
        timestamp: new Date().toISOString()
      }
    }));

    // Update vehicles state with new location
    setVehicles(prev => prev.map(vehicle => {
      if (vehicle._id === data.vehicleId || vehicle.id === data.vehicleId) {
        return {
          ...vehicle,
          currentLocation: {
            coordinates: data.location?.coordinates || data.coordinates || [0, 0],
            address: data.location?.address || data.address || vehicle.currentLocation?.address || 'Unknown',
            timestamp: new Date().toISOString(),
            speed: data.speed || 0,
            heading: data.heading || 0
          },
          lastLocationUpdate: new Date().toISOString()
        };
      }
      return vehicle;
    }));
  };

  const handleVehicleAssignment = (data) => {
    setVehicles(prev => prev.map(vehicle => {
      if (vehicle._id === data.vehicleId || vehicle.id === data.vehicleId) {
        return {
          ...vehicle,
          assignedDriver: data.driverId ? {
            _id: data.driverId,
            name: data.driverName
          } : null,
          status: data.driverId ? 'on_trip' : 'available'
        };
      }
      return vehicle;
    }));
  };

  const handleVehicleUnassignment = (data) => {
    setVehicles(prev => prev.map(vehicle => {
      if (vehicle._id === data.vehicleId || vehicle.id === data.vehicleId) {
        return {
          ...vehicle,
          assignedDriver: null,
          status: 'available'
        };
      }
      return vehicle;
    }));
  };

  const handleTrackingUpdate = (vehicleId, isTracking) => {
    setTrackingVehicles(prev => ({
      ...prev,
      [vehicleId]: isTracking
    }));
  };

  const showAlertNotification = (alert) => {
    // Implement notification system
    console.log('Alert:', alert);
    // Could use a toast notification library here
  };

  // Fetch vehicles using API
  const fetchVehicles = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Test connection first
      await testBackendConnection();

      const response = await vehiclesAPI.getAll();
      
      if (response.success) {
        const vehiclesData = response.data || [];
        setVehicles(vehiclesData);
        
        // Initialize tracking status from vehicle data
        const initialTracking = {};
        vehiclesData.forEach(vehicle => {
          const vehicleId = vehicle._id || vehicle.id;
          initialTracking[vehicleId] = vehicle.trackingSettings?.autoTracking || false;
        });
        setTrackingVehicles(initialTracking);
      } else {
        setError(response.message || 'Failed to fetch vehicles');
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setError(error.message || 'Error connecting to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Test backend connection
  const testBackendConnection = async () => {
    try {
      const response = await testConnection();
      console.log('Backend connection test:', response);
    } catch (error) {
      console.error('Backend connection failed:', error);
      setError('Cannot connect to backend server. Please try again later.');
    }
  };

  // Fetch available drivers using API
  const fetchAvailableDrivers = async () => {
    try {
      // Try to get available drivers from drivers API
      const response = await driversAPI.getAll();
      
      if (response.success) {
        const allDrivers = response.data || [];
        // Filter drivers that are not assigned to any vehicle
        const assignedDriverIds = new Set(
          vehicles
            .filter(v => v.assignedDriver && v.assignedDriver._id)
            .map(v => v.assignedDriver._id)
        );
        
        const available = allDrivers.filter(
          driver => !assignedDriverIds.has(driver._id) && 
                   (driver.status === 'active' || driver.status === 'available')
        );
        
        setAvailableDrivers(available);
      }
    } catch (error) {
      console.error('Error fetching available drivers:', error);
      // Fallback: try direct endpoint
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api'}/drivers/available`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setAvailableDrivers(data.data || []);
          }
        }
      } catch (fetchError) {
        console.error('Error in fallback fetch:', fetchError);
      }
    }
  };

  // Create new vehicle using API
  const createVehicle = async () => {
    try {
      setError('');
      
      // Validate required fields
      if (!newVehicle.registration || !newVehicle.model || !newVehicle.vehicleType) {
        setError('Please fill in all required fields');
        return;
      }

      const response = await vehiclesAPI.create(newVehicle);

      if (response.success) {
        // Show success modal
        setSuccessModalData({
          title: 'Vehicle Added Successfully!',
          message: `Vehicle ${newVehicle.registration} has been created and added to the fleet.`,
          details: `${newVehicle.model} (${newVehicle.year}) is now available in the system.`,
          type: 'success'
        });
        setShowSuccessModal(true);
        
        setShowAddVehicleModal(false);
        resetNewVehicleForm();
        fetchVehicles();
      } else {
        setError(response.message || 'Failed to create vehicle');
      }
    } catch (error) {
      console.error('Error creating vehicle:', error);
      setError(error.message || 'Error creating vehicle. Please try again.');
    }
  };

  const resetNewVehicleForm = () => {
    setNewVehicle({
      registration: '',
      model: '',
      year: new Date().getFullYear(),
      vehicleType: 'truck',
      fuelLevel: 0,
      mileage: 0,
      specifications: {
        engineSize: '',
        fuelType: 'diesel',
        capacity: '',
        color: ''
      }
    });
  };

  // Assign driver to vehicle using API
// Assign driver to vehicle using API
const assignDriverToVehicle = async (vehicleId, driverId) => {
  try {
    setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
    setError('');
    
    const response = await vehiclesAPI.assignDriver(vehicleId, { driverId });

    if (response.success) {
      const driver = availableDrivers.find(d => d._id === driverId || d.id === driverId);
      const driverName = driver?.name || driver?.user?.profile?.name || driver?.user?.name || 'Unknown Driver';
      
      // Find the vehicle from vehicles state since selectedVehicle might not be accessible
      const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId);
      
      setSuccessModalData({
        title: 'Driver Assigned Successfully!',
        message: `${driverName} has been assigned to vehicle ${vehicle?.registration || ''}.`,
        details: `${vehicle?.model || 'Vehicle'} is now under ${driverName}'s responsibility.`,
        driverName: driverName,
        vehicleRegistration: vehicle?.registration,
        vehicleModel: vehicle?.model,
        type: 'success'
      });
      setShowSuccessModal(true);
      
      setShowAssignDriverModal(false);
      setSelectedDriverId('');
      fetchVehicles(); // Refresh vehicles list
    } else {
      setError(response.message || 'Failed to assign driver');
    }
  } catch (error) {
    console.error('Error assigning driver:', error);
    setError(error.message || 'Error assigning driver. Please try again.');
  } finally {
    setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
  }
};

  // Unassign driver from vehicle using API
  const unassignDriverFromVehicle = async (vehicleId) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
      setError('');
      
      const response = await vehiclesAPI.unassignDriver(vehicleId);

      if (response.success) {
        const vehicle = vehicles.find(v => v._id === vehicleId || v.id === vehicleId);
        const driverName = vehicle?.assignedDriver?.name || 'Driver';
        
        setSuccessModalData({
          title: 'Driver Unassigned Successfully!',
          message: `${driverName} has been unassigned from vehicle ${vehicle?.registration || ''}.`,
          details: `${vehicle?.registration || 'Vehicle'} is now available for new driver assignment.`,
          type: 'success'
        });
        setShowSuccessModal(true);
        
        fetchVehicles(); // Refresh vehicles list
      } else {
        setError(response.message || 'Failed to unassign driver');
      }
    } catch (error) {
      console.error('Error unassigning driver:', error);
      setError(error.message || 'Error unassigning driver. Please try again.');
    } finally {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  // Start vehicle tracking using API
  const startVehicleTracking = async (vehicleId) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
      setError('');
      
      // Check if vehicle is assigned to a driver
      const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId);
      if (!vehicle?.assignedDriver) {
        setError('Please assign a driver before starting tracking');
        setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
        return;
      }

      // Enable auto-tracking on the vehicle
      // Note: You may need to create this endpoint in your backend
      try {
        const response = await vehiclesAPI.update(vehicleId, {
          trackingSettings: { autoTracking: true }
        });

        if (response.success) {
          setTrackingVehicles(prev => ({
            ...prev,
            [vehicleId]: true
          }));
          
          // Show success modal
          setSuccessModalData({
            title: 'Tracking Started Successfully!',
            message: `Real-time tracking has been activated for ${vehicle?.registration || 'Vehicle'}.`,
            details: `You can now monitor ${vehicle?.registration}'s location and route in real-time.`,
            type: 'success'
          });
          setShowSuccessModal(true);
          
          // Also start driver tracking if driver is assigned
          if (vehicle.assignedDriver) {
            await startDriverTracking(vehicle.assignedDriver._id || vehicle.assignedDriver.id, vehicleId);
          }
        } else {
          setError(response.message || 'Failed to start tracking');
        }
      } catch (apiError) {
        // Fallback to direct endpoint
        const token = localStorage.getItem('token');
        const fallbackResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api'}/vehicles/${vehicleId}/enable-tracking`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (fallbackResponse.ok) {
          const data = await fallbackResponse.json();
          if (data.success) {
            setTrackingVehicles(prev => ({
              ...prev,
              [vehicleId]: true
            }));
            
            setSuccessModalData({
              title: 'Tracking Started Successfully!',
              message: `Real-time tracking has been activated for ${vehicle?.registration || 'Vehicle'}.`,
              details: `You can now monitor ${vehicle?.registration}'s location and route in real-time.`,
              type: 'success'
            });
            setShowSuccessModal(true);
          } else {
            setError(data.message || 'Failed to start tracking');
          }
        } else {
          setError('Failed to start tracking');
        }
      }
    } catch (error) {
      console.error('Error starting tracking:', error);
      setError(error.message || 'Error starting tracking. Please try again.');
    } finally {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  // Start driver tracking (linked to vehicle)
  const startDriverTracking = async (driverId, vehicleId) => {
    try {
      // Note: You may need to create this endpoint in your backend
      const response = await driversAPI.update(driverId, {
        trackingSettings: { autoTracking: true, vehicleId }
      });
      
      if (!response.success) {
        console.error('Failed to start driver tracking');
      }
    } catch (error) {
      console.error('Error starting driver tracking:', error);
    }
  };

  // Stop vehicle tracking using API
 // Stop vehicle tracking using API
const stopVehicleTracking = async (vehicleId) => {
  try {
    setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
    setError('');
    
    // Check if vehicle is assigned to a driver - find vehicle first
    const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId);
    
    // Disable auto-tracking on the vehicle
    try {
      const response = await vehiclesAPI.update(vehicleId, {
        trackingSettings: { autoTracking: false }
      });

      if (response.success) {
        setTrackingVehicles(prev => ({
          ...prev,
          [vehicleId]: false
        }));
        
        // Show success modal
        setSuccessModalData({
          title: 'Tracking Stopped Successfully!',
          message: `Real-time tracking has been deactivated for ${vehicle?.registration || 'Vehicle'}.`,
          details: `${vehicle?.registration || 'The vehicle'} is no longer being tracked in real-time.`,
          type: 'info'
        });
        setShowSuccessModal(true);
        
        // Also stop driver tracking
        if (vehicle?.assignedDriver) {
          await stopDriverTracking(vehicle.assignedDriver._id || vehicle.assignedDriver.id);
        }
      } else {
        setError(response.message || 'Failed to stop tracking');
      }
    } catch (apiError) {
      // Fallback to direct endpoint
      const token = localStorage.getItem('token');
      const fallbackResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://fleet-county-backend.onrender.com/api'}/vehicles/${vehicleId}/disable-tracking`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (fallbackResponse.ok) {
        const data = await fallbackResponse.json();
        if (data.success) {
          setTrackingVehicles(prev => ({
            ...prev,
            [vehicleId]: false
          }));
          
          setSuccessModalData({
            title: 'Tracking Stopped Successfully!',
            message: `Real-time tracking has been deactivated for ${vehicle?.registration || 'Vehicle'}.`,
            details: `${vehicle?.registration || 'The vehicle'} is no longer being tracked in real-time.`,
            type: 'info'
          });
          setShowSuccessModal(true);
        } else {
          setError(data.message || 'Failed to stop tracking');
        }
      } else {
        setError('Failed to stop tracking');
      }
    }
  } catch (error) {
    console.error('Error stopping tracking:', error);
    setError(error.message || 'Error stopping tracking. Please try again.');
  } finally {
    setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
  }
};

  // Stop driver tracking
  const stopDriverTracking = async (driverId) => {
    try {
      // Note: You may need to create this endpoint in your backend
      const response = await driversAPI.update(driverId, {
        trackingSettings: { autoTracking: false }
      });

      if (!response.success) {
        console.error('Failed to stop driver tracking');
      }
    } catch (error) {
      console.error('Error stopping driver tracking:', error);
    }
  };

  // Update vehicle location using API
  const updateVehicleLocation = async (vehicleId, coordinates, address) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
      setError('');
      
      const response = await vehiclesAPI.update(vehicleId, {
        currentLocation: {
          coordinates: coordinates,
          address: address || "Updated location",
          timestamp: new Date().toISOString()
        }
      });

      if (response.success) {
        // Update local state immediately
        setVehicles(prev => prev.map(vehicle => {
          if ((vehicle._id || vehicle.id) === vehicleId) {
            return {
              ...vehicle,
              currentLocation: {
                coordinates: coordinates,
                address: address || vehicle.currentLocation?.address || "Updated location",
                timestamp: new Date().toISOString()
              }
            };
          }
          return vehicle;
        }));
        
        // Show success modal
        const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId);
        setSuccessModalData({
          title: 'Location Updated Successfully!',
          message: `Location updated for ${vehicle?.registration || 'Vehicle'}.`,
          details: address || 'New coordinates set successfully.',
          type: 'success'
        });
        setShowSuccessModal(true);
        
        // Send WebSocket update
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'location_update',
            vehicleId: vehicleId,
            coordinates: coordinates,
            address: address,
            timestamp: new Date().toISOString()
          }));
        }
      } else {
        setError(response.message || 'Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      setError(error.message || 'Error updating location. Please try again.');
    } finally {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  // Update vehicle fuel using API
  const updateVehicleFuel = async (vehicleId, fuelLevel) => {
    try {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: true }));
      setError('');
      
      const response = await vehiclesAPI.update(vehicleId, { fuelLevel });

      if (response.success) {
        // Update local state immediately
        setVehicles(prev => prev.map(vehicle => {
          if ((vehicle._id || vehicle.id) === vehicleId) {
            return { ...vehicle, fuelLevel };
          }
          return vehicle;
        }));
        
        // Show success modal
        const vehicle = vehicles.find(v => (v._id || v.id) === vehicleId);
        setSuccessModalData({
          title: 'Fuel Level Updated Successfully!',
          message: `Fuel level updated for ${vehicle?.registration || 'Vehicle'}.`,
          details: `New fuel level: ${fuelLevel}%`,
          type: 'success'
        });
        setShowSuccessModal(true);
      } else {
        setError(response.message || 'Failed to update fuel level');
      }
    } catch (error) {
      console.error('Error updating fuel level:', error);
      setError(error.message || 'Error updating fuel level. Please try again.');
    } finally {
      setUpdateLoading(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  // Calculate service status
  const getServiceStatus = (lastServiceDate) => {
    if (!lastServiceDate) return 'unknown';
    
    const lastService = new Date(lastServiceDate);
    const today = new Date();
    const daysSinceService = Math.floor((today - lastService) / (1000 * 60 * 60 * 24));
    
    if (daysSinceService > 365) return 'overdue';
    if (daysSinceService > 300) return 'due_soon';
    return 'ok';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'in_transit':
      case 'active':
      case 'on_trip':
      case 'on_duty':
      case 'moving':
      case 'in_use':
        return 'bg-blue-100 text-blue-800';
      case 'available':
      case 'idle':
      case 'ready':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
      case 'inactive':
      case 'off_duty':
      case 'out_of_service':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ').toUpperCase();
  };

  // Get service status color
  const getServiceStatusColor = (status) => {
    switch (status) {
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'due_soon': return 'bg-orange-100 text-orange-800';
      case 'ok': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get service status text
  const getServiceStatusText = (status) => {
    switch (status) {
      case 'overdue': return 'Service Overdue';
      case 'due_soon': return 'Service Due Soon';
      case 'ok': return 'Service OK';
      default: return 'Service Unknown';
    }
  };

  // Get connectivity status
  const getConnectivityStatus = (vehicleId) => {
    const realtime = realtimeData[vehicleId];
    if (!realtime) return 'disconnected';
    
    const updateTime = new Date(realtime.timestamp);
    const now = new Date();
    const secondsSinceUpdate = (now - updateTime) / 1000;
    
    if (secondsSinceUpdate < 60) return 'excellent';
    if (secondsSinceUpdate < 180) return 'good';
    if (secondsSinceUpdate < 300) return 'fair';
    return 'poor';
  };

  // Get connectivity color
  const getConnectivityColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-green-500';
      case 'good': return 'bg-green-400';
      case 'fair': return 'bg-yellow-500';
      case 'poor': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Calculate days ago
  const getDaysAgo = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get driver name
  const getDriverName = (assignedDriver) => {
    if (!assignedDriver) return 'N/A';
    
    if (assignedDriver.name) return assignedDriver.name;
    if (assignedDriver.user?.profile?.name) return assignedDriver.user.profile.name;
    if (assignedDriver.user?.name) return assignedDriver.user.name;
    return assignedDriver.driverId || 'Unknown Driver';
  };

  // Filter vehicles
  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      (vehicle.registration || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vehicle.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vehicle.vehicleType || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      filterStatus === 'all' || 
      (vehicle.status || '').toLowerCase() === filterStatus.toLowerCase();
    
    const matchesType = 
      filterType === 'all' || 
      (vehicle.vehicleType || '').toLowerCase() === filterType.toLowerCase();
    
    return matchesSearch && matchesStatus && matchesType;
  });

  // Get vehicle types for filter
  const vehicleTypes = [...new Set(vehicles.map(v => v.vehicleType).filter(Boolean))];

  // Success Modal Component
  const renderSuccessModal = () => {
    if (!showSuccessModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-xl w-full max-w-md transform transition-all duration-300 scale-100">
          {/* Modal Content */}
          <div className="p-6 text-center">
            {/* Success Icon */}
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              {successModalData.type === 'success' ? (
                <CheckCircle className="h-10 w-10 text-green-600" />
              ) : (
                <CheckCircle className="h-10 w-10 text-blue-600" />
              )}
            </div>
            
            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              {successModalData.title}
            </h3>
            
            {/* Message */}
            <p className="text-gray-600 mb-4">
              {successModalData.message}
            </p>
            
            {/* Additional Details */}
            {successModalData.details && (
              <p className="text-gray-500 text-sm mb-6">
                {successModalData.details}
              </p>
            )}
            
            {/* Driver and Vehicle Info (for assignment success) */}
            {(successModalData.driverName && successModalData.vehicleRegistration) && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#e28743] rounded-full flex items-center justify-center">
                      <User size={20} className="text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm text-gray-500">Driver</p>
                      <p className="font-semibold">{successModalData.driverName}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 text-2xl text-gray-300">
                    <ChevronRight />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Vehicle</p>
                      <p className="font-semibold">{successModalData.vehicleRegistration}</p>
                      {successModalData.vehicleModel && (
                        <p className="text-xs text-gray-500">{successModalData.vehicleModel}</p>
                      )}
                    </div>
                    <div className="w-10 h-10 bg-[#304a3f] rounded-full flex items-center justify-center">
                      <Truck size={20} className="text-white" />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="h-1 w-12 bg-[#e28743] rounded-full"></div>
                  <CheckCircle size={16} className="text-green-500" />
                  <div className="h-1 w-12 bg-[#304a3f] rounded-full"></div>
                </div>
              </div>
            )}
            
            {/* Action Button */}
            <button
              onClick={() => setShowSuccessModal(false)}
              className="w-full py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors font-semibold"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAssignDriverModal = () => {
    if (!selectedVehicle) return null;

    const vehicleId = selectedVehicle._id || selectedVehicle.id;
    const currentDriver = selectedVehicle.assignedDriver;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">
                {currentDriver ? 'Change Driver' : 'Assign Driver'}
              </h2>
              <button
                onClick={() => {
                  setShowAssignDriverModal(false);
                  setSelectedDriverId('');
                  setError('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Current Driver Info */}
            {currentDriver && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <h3 className="font-semibold text-blue-800 mb-2">Current Driver</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{getDriverName(currentDriver)}</p>
                    <p className="text-sm text-gray-600">
                      {currentDriver.driverId || 'N/A'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to unassign this driver?')) {
                        unassignDriverFromVehicle(vehicleId);
                        setShowAssignDriverModal(false);
                      }
                    }}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                    disabled={updateLoading[vehicleId]}
                  >
                    {updateLoading[vehicleId] ? '...' : 'Unassign'}
                  </button>
                </div>
              </div>
            )}

            {/* Driver Selection */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Select a Driver</h3>
              
              {availableDrivers.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-xl">
                  <User size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No available drivers</p>
                  <p className="text-sm text-gray-500 mt-2">All drivers are currently assigned to vehicles</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availableDrivers.map(driver => {
                    const driverId = driver._id || driver.id;
                    const driverName = driver.name || driver.user?.profile?.name || driver.user?.name || 'Unknown';
                    
                    return (
                      <div
                        key={driverId}
                        className={`p-4 border rounded-xl cursor-pointer transition-all ${
                          selectedDriverId === driverId
                            ? 'border-[#e28743] bg-orange-50'
                            : 'border-gray-200 hover:border-[#e28743] hover:bg-orange-50'
                        }`}
                        onClick={() => setSelectedDriverId(driverId)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#e28743] rounded-full flex items-center justify-center">
                            <User size={20} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{driverName}</p>
                            <p className="text-sm text-gray-500">{driver.driverId || 'N/A'}</p>
                            <p className="text-xs text-gray-400">
                              {driver.licenseNumber || 'No license'}
                            </p>
                          </div>
                          {selectedDriverId === driverId && (
                            <div className="w-6 h-6 bg-[#e28743] rounded-full flex items-center justify-center">
                              <CheckCircle size={14} className="text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-8 pt-6 border-t flex justify-end gap-3">
              <button
                className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => {
                  setShowAssignDriverModal(false);
                  setSelectedDriverId('');
                  setError('');
                }}
              >
                Cancel
              </button>
              <button
                className="px-6 py-3 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={() => assignDriverToVehicle(vehicleId, selectedDriverId)}
                disabled={!selectedDriverId || updateLoading[vehicleId]}
              >
                {updateLoading[vehicleId] ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Assigning...
                  </>
                ) : (
                  'Assign Driver'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render connectivity indicator
  const renderConnectivityIndicator = (vehicleId) => {
    const status = getConnectivityStatus(vehicleId);
    const realtime = realtimeData[vehicleId];
    
    return (
      <div className="flex items-center gap-1">
        <div className={`w-2 h-2 rounded-full ${getConnectivityColor(status)}`}></div>
        <span className="text-xs text-gray-500">
          {realtime ? 
            `Updated ${Math.floor((new Date() - new Date(realtime.timestamp)) / 1000)}s ago` : 
            'No data'
          }
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e28743] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vehicles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      {renderSuccessModal()}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
          <button
            onClick={() => setError('')}
            className="text-red-700 hover:text-red-900"
          >
            <X size={20} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vehicle Management</h1>
          <p className="text-gray-600">Monitor and manage all vehicles in real-time</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <button 
            className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] flex items-center gap-2"
            onClick={() => setShowAddVehicleModal(true)}
          >
            <Plus size={18} />
            Add Vehicle
          </button>
        </div>
      </div>

      {/* Stats Summary - Compact */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white p-3 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total Vehicles</p>
              <p className="text-lg font-bold">{vehicles.length}</p>
            </div>
            <Truck className="text-[#e28743]" size={20} />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Available</p>
              <p className="text-lg font-bold text-green-600">
                {vehicles.filter(v => v.status === 'available').length}
              </p>
            </div>
            <Truck className="text-green-500" size={20} />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">In Use</p>
              <p className="text-lg font-bold text-[#304a3f]">
                {vehicles.filter(v => ['in_transit', 'on_trip', 'active', 'moving', 'in_use'].includes(v.status)).length}
              </p>
            </div>
            <Navigation className="text-[#304a3f]" size={20} />
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Tracking Active</p>
              <p className="text-lg font-bold text-purple-600">
                {Object.values(trackingVehicles).filter(v => v).length}
              </p>
            </div>
            <Radio className="text-purple-500" size={20} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search vehicles by registration, model, or type..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743] text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743] text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
              <option value="active">Active</option>
              <option value="in_use">In Use</option>
              <option value="on_trip">On Trip</option>
              <option value="in_transit">In Transit</option>
              <option value="maintenance">Maintenance</option>
              <option value="inactive">Inactive</option>
            </select>
            
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743] text-sm"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All Types</option>
              {vehicleTypes.map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Vehicles Grid - Compact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredVehicles.map((vehicle) => {
          const vehicleId = vehicle._id || vehicle.id;
          const serviceStatus = getServiceStatus(vehicle.lastService);
          const daysSinceService = getDaysAgo(vehicle.lastService);
          const isTracking = trackingVehicles[vehicleId] || vehicle.trackingSettings?.autoTracking;
          const isExpanded = expandedCard === vehicleId;
          const assignedDriver = vehicle.assignedDriver;
          const realtime = realtimeData[vehicleId];
          const showControls = showTrackingControls[vehicleId];
          
          return (
            <div 
              key={vehicleId} 
              className={`bg-white rounded-lg shadow-sm border overflow-hidden transition-all duration-200 ${
                isExpanded ? 'ring-2 ring-[#e28743]' : ''
              } ${isTracking ? 'border-[#304a3f]' : ''}`}
            >
              {/* Card Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Truck size={20} className="text-[#e28743]" />
                      </div>
                      {isTracking && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm">
                        {vehicle.registration || 'N/A'}
                      </h3>
                      <p className="text-xs text-gray-500">{vehicle.model}</p>
                      {renderConnectivityIndicator(vehicleId)}
                    </div>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(vehicle.status)}`}>
                      {getStatusText(vehicle.status)}
                    </span>
                    <button
                      onClick={() => setExpandedCard(isExpanded ? null : vehicleId)}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <ChevronRight size={16} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="text-sm font-medium">
                      {vehicle.vehicleType ? vehicle.vehicleType.toUpperCase() : 'N/A'}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Year</p>
                    <p className="text-sm font-medium">{vehicle.year || 'N/A'}</p>
                  </div>
                </div>

                {/* Fuel & Mileage */}
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <div className="flex items-center gap-1">
                        <Fuel size={14} className="text-gray-400" />
                        <span className="text-xs font-medium text-gray-700">Fuel</span>
                      </div>
                      <span className="text-xs font-bold">{vehicle.fuelLevel || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5">
                      <div 
                        className={`h-1.5 rounded-full ${
                          vehicle.fuelLevel > 50 ? 'bg-green-500' : 
                          vehicle.fuelLevel > 20 ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${vehicle.fuelLevel || 0}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <Gauge size={14} className="text-gray-400" />
                      <span className="text-xs text-gray-600">Mileage:</span>
                    </div>
                    <span className="text-xs font-medium">
                      {(vehicle.mileage || vehicle.odometer || 0).toLocaleString()} km
                    </span>
                  </div>
                </div>

                {/* Assigned Driver */}
                {assignedDriver && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-gray-400" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Assigned Driver</p>
                          <p className="text-xs font-medium truncate">
                            {getDriverName(assignedDriver)}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          if (window.confirm('Unassign this driver?')) {
                            unassignDriverFromVehicle(vehicleId);
                          }
                        }}
                        className="text-xs text-red-600 hover:text-red-800"
                        disabled={updateLoading[vehicleId]}
                      >
                        {updateLoading[vehicleId] ? '...' : 'Unassign'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="mt-4 pt-4 border-t animate-slideDown">
                    <div className="space-y-3">
                      {/* Location */}
                      {(vehicle.currentLocation || realtime) && (
                        <div className="flex items-start gap-2">
                          <MapPin size={14} className="text-gray-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-700">Location</p>
                            <p className="text-xs text-gray-600 truncate">
                              {realtime?.address || 
                               vehicle.currentLocation?.address || 
                               `Lat: ${realtime?.coordinates?.[1] || vehicle.currentLocation?.coordinates?.[1] || '0'}, Lng: ${realtime?.coordinates?.[0] || vehicle.currentLocation?.coordinates?.[0] || '0'}`}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Last Update */}
                      {(vehicle.currentLocation?.timestamp || realtime?.timestamp) && (
                        <div className="flex items-center gap-2">
                          <Clock size={14} className="text-gray-400" />
                          <div>
                            <p className="text-xs font-medium text-gray-700">Last Update</p>
                            <p className="text-xs text-gray-600">
                              {new Date(realtime?.timestamp || vehicle.currentLocation?.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Service Status */}
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400" />
                        <div>
                          <p className="text-xs font-medium text-gray-700">Service</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600">
                              {formatDate(vehicle.lastService)}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded-full text-xs ${getServiceStatusColor(serviceStatus)}`}>
                              {getServiceStatusText(serviceStatus)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tracking Status */}
                      <div className="flex items-center gap-2">
                        <Activity size={14} className="text-gray-400" />
                        <div>
                          <p className="text-xs font-medium text-gray-700">Tracking</p>
                          <span className={`text-xs ${isTracking ? 'text-green-600' : 'text-gray-600'}`}>
                            {isTracking ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 flex flex-col gap-2">
                      {!assignedDriver && (
                        <button
                          className="w-full py-2 px-3 bg-[#304a3f] text-white rounded-lg hover:bg-[#253830] text-xs flex items-center justify-center gap-1"
                          onClick={() => {
                            setSelectedVehicle(vehicle);
                            fetchAvailableDrivers();
                            setShowAssignDriverModal(true);
                          }}
                        >
                          <User size={12} />
                          Assign Driver
                        </button>
                      )}
                      
                      <div className="flex gap-2">
                        <button
                          className={`flex-1 py-2 px-3 rounded-lg text-xs flex items-center justify-center gap-1 ${
                            isTracking 
                              ? 'bg-red-600 text-white hover:bg-red-700' 
                              : 'bg-[#e28743] text-white hover:bg-[#d47834]'
                          }`}
                          onClick={() => {
                            if (isTracking) {
                              stopVehicleTracking(vehicleId);
                            } else {
                              startVehicleTracking(vehicleId);
                            }
                          }}
                          disabled={updateLoading[vehicleId]}
                        >
                          {updateLoading[vehicleId] ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                              Loading...
                            </>
                          ) : isTracking ? (
                            <>
                              <AlertCircle size={12} />
                              Stop Track
                            </>
                          ) : (
                            <>
                              <Play size={12} />
                              Track
                            </>
                          )}
                        </button>
                        
                        <button
                          className="flex-1 py-2 px-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-xs flex items-center justify-center gap-1"
                          onClick={() => {
                            setSelectedVehicle(vehicle);
                            setShowDetailsModal(true);
                          }}
                        >
                          <Eye size={12} />
                          Details
                        </button>
                      </div>

                      {/* Quick Location Update */}
                      <button
                        className="w-full py-2 px-3 border border-[#304a3f] text-[#304a3f] rounded-lg hover:bg-[#304a3f] hover:text-white text-xs flex items-center justify-center gap-1 transition-colors"
                        onClick={() => {
                          const lat = parseFloat(prompt('Enter latitude:', vehicle.currentLocation?.coordinates?.[1] || '0'));
                          const lng = parseFloat(prompt('Enter longitude:', vehicle.currentLocation?.coordinates?.[0] || '0'));
                          const address = prompt('Enter address (optional):', vehicle.currentLocation?.address || '');
                          if (!isNaN(lat) && !isNaN(lng)) {
                            updateVehicleLocation(vehicleId, [lng, lat], address);
                          }
                        }}
                        disabled={updateLoading[vehicleId]}
                      >
                        <MapPin size={12} />
                        Update Location
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions (when not expanded) */}
                {!isExpanded && (
                  <div className="mt-4 flex gap-2">
                    {!assignedDriver && (
                      <button
                        className="flex-1 py-1.5 px-2 bg-[#304a3f] text-white rounded hover:bg-[#253830] text-xs flex items-center justify-center gap-1"
                        onClick={() => {
                          setSelectedVehicle(vehicle);
                          fetchAvailableDrivers();
                          setShowAssignDriverModal(true);
                        }}
                        disabled={updateLoading[vehicleId]}
                      >
                        <User size={12} />
                        Assign
                      </button>
                    )}
                    
                    <button
                      className={`flex-1 py-1.5 px-2 rounded text-xs flex items-center justify-center gap-1 ${
                        isTracking 
                          ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                          : 'bg-[#e28743] text-white hover:bg-[#d47834]'
                      }`}
                      onClick={() => {
                        if (isTracking) {
                          stopVehicleTracking(vehicleId);
                        } else {
                          startVehicleTracking(vehicleId);
                        }
                      }}
                      disabled={updateLoading[vehicleId]}
                    >
                      {updateLoading[vehicleId] ? '...' : isTracking ? 'Stop' : 'Track'}
                    </button>
                    
                    <button
                      className="flex-1 py-1.5 px-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 text-xs"
                      onClick={() => {
                        setSelectedVehicle(vehicle);
                        setShowDetailsModal(true);
                      }}
                    >
                      Details
                    </button>
                    
                    <button
                      className="p-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                      onClick={() => setExpandedCard(vehicleId)}
                    >
                      <MoreVertical size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Vehicle Modal */}
      {showAddVehicleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-800">Add New Vehicle</h2>
                <button
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    resetNewVehicleForm();
                    setError('');
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Number *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  value={newVehicle.registration}
                  onChange={(e) => setNewVehicle({...newVehicle, registration: e.target.value.toUpperCase()})}
                  placeholder="ABC-123-XY"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make & Model *
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                  placeholder="Toyota Hiace"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    value={newVehicle.year}
                    onChange={(e) => setNewVehicle({...newVehicle, year: parseInt(e.target.value)})}
                    min="2000"
                    max={new Date().getFullYear() + 1}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Type *
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    value={newVehicle.vehicleType}
                    onChange={(e) => setNewVehicle({...newVehicle, vehicleType: e.target.value})}
                  >
                    <option value="truck">Truck</option>
                    <option value="van">Van</option>
                    <option value="bus">Bus</option>
                    <option value="car">Car</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fuel Level (%)
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    className="w-full"
                    value={newVehicle.fuelLevel}
                    onChange={(e) => setNewVehicle({...newVehicle, fuelLevel: parseInt(e.target.value)})}
                  />
                  <div className="text-center mt-1">
                    <span className="text-sm font-medium">{newVehicle.fuelLevel}%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mileage (km)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                    value={newVehicle.mileage}
                    onChange={(e) => setNewVehicle({...newVehicle, mileage: parseInt(e.target.value)})}
                    min="0"
                  />
                </div>
              </div>

              {/* Specifications */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Specifications</h3>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Engine Size
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                        value={newVehicle.specifications.engineSize}
                        onChange={(e) => setNewVehicle({
                          ...newVehicle, 
                          specifications: {...newVehicle.specifications, engineSize: e.target.value}
                        })}
                        placeholder="2.8L"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fuel Type
                      </label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                        value={newVehicle.specifications.fuelType}
                        onChange={(e) => setNewVehicle({
                          ...newVehicle, 
                          specifications: {...newVehicle.specifications, fuelType: e.target.value}
                        })}
                      >
                        <option value="diesel">Diesel</option>
                        <option value="petrol">Petrol</option>
                        <option value="electric">Electric</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacity
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                        value={newVehicle.specifications.capacity}
                        onChange={(e) => setNewVehicle({
                          ...newVehicle, 
                          specifications: {...newVehicle.specifications, capacity: e.target.value}
                        })}
                        placeholder="15 passengers"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Color
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e28743]"
                        value={newVehicle.specifications.color}
                        onChange={(e) => setNewVehicle({
                          ...newVehicle, 
                          specifications: {...newVehicle.specifications, color: e.target.value}
                        })}
                        placeholder="White"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t p-6">
              <div className="flex justify-end gap-3">
                <button
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                  onClick={() => {
                    setShowAddVehicleModal(false);
                    resetNewVehicleForm();
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={createVehicle}
                  disabled={!newVehicle.registration || !newVehicle.model || !newVehicle.vehicleType}
                >
                  Create Vehicle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Driver Modal */}
      {showAssignDriverModal && renderAssignDriverModal()}

      {/* Vehicle Details Modal */}
      {showDetailsModal && selectedVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center relative">
                    <Truck size={28} className="text-[#e28743]" />
                    {trackingVehicles[selectedVehicle._id || selectedVehicle.id] && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                        <Radio size={10} className="text-white" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{selectedVehicle.registration}</h2>
                    <p className="text-gray-600">{selectedVehicle.model}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedVehicle.status)}`}>
                        {getStatusText(selectedVehicle.status)}
                      </span>
                      {renderConnectivityIndicator(selectedVehicle._id || selectedVehicle.id)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Status Card */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Status Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Vehicle Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedVehicle.status)}`}>
                          {getStatusText(selectedVehicle.status)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tracking Status</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          trackingVehicles[selectedVehicle._id || selectedVehicle.id] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {trackingVehicles[selectedVehicle._id || selectedVehicle.id] ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Year</span>
                        <span className="font-medium">{selectedVehicle.year}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Type</span>
                        <span className="font-medium">{selectedVehicle.vehicleType?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Fuel Card */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-800">Fuel Status</h3>
                      <div className="flex items-center gap-2">
                        <Fuel size={20} className="text-[#e28743]" />
                        <span className="text-2xl font-bold">{selectedVehicle.fuelLevel || 0}%</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                      <div 
                        className={`h-3 rounded-full ${
                          selectedVehicle.fuelLevel > 50 ? 'bg-green-500' : 
                          selectedVehicle.fuelLevel > 20 ? 'bg-yellow-500' : 
                          'bg-red-500'
                        }`}
                        style={{ width: `${selectedVehicle.fuelLevel || 0}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Empty</span>
                      <span>Full</span>
                    </div>
                    <button
                      className="mt-4 w-full py-2 border border-[#e28743] text-[#e28743] rounded-lg hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        const newFuelLevel = prompt('Enter new fuel level (0-100):', selectedVehicle.fuelLevel || 0);
                        if (newFuelLevel !== null) {
                          const fuelLevel = parseInt(newFuelLevel);
                          if (!isNaN(fuelLevel) && fuelLevel >= 0 && fuelLevel <= 100) {
                            updateVehicleFuel(selectedVehicle._id || selectedVehicle.id, fuelLevel);
                          } else {
                            alert('Please enter a valid fuel level between 0 and 100');
                          }
                        }
                      }}
                      disabled={updateLoading[selectedVehicle._id || selectedVehicle.id]}
                    >
                      {updateLoading[selectedVehicle._id || selectedVehicle.id] ? 'Updating...' : 'Update Fuel Level'}
                    </button>
                  </div>

                  {/* Location Card */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Current Location</h3>
                    {selectedVehicle.currentLocation || realtimeData[selectedVehicle._id || selectedVehicle.id] ? (
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin size={20} className="text-gray-400 mt-1" />
                          <div>
                            <p className="text-gray-700">
                              {realtimeData[selectedVehicle._id || selectedVehicle.id]?.address || 
                               selectedVehicle.currentLocation?.address || 
                               'Unknown location'}
                            </p>
                            <p className="text-sm text-gray-500">
                              Coordinates: {
                                realtimeData[selectedVehicle._id || selectedVehicle.id]?.coordinates?.[1] || 
                                selectedVehicle.currentLocation?.coordinates?.[1] || 
                                '0'
                              }, {
                                realtimeData[selectedVehicle._id || selectedVehicle.id]?.coordinates?.[0] || 
                                selectedVehicle.currentLocation?.coordinates?.[0] || 
                                '0'
                              }
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock size={16} />
                          <span>
                            Last updated: {
                              realtimeData[selectedVehicle._id || selectedVehicle.id]?.timestamp ? 
                                new Date(realtimeData[selectedVehicle._id || selectedVehicle.id].timestamp).toLocaleString() :
                              selectedVehicle.currentLocation?.timestamp ? 
                                new Date(selectedVehicle.currentLocation.timestamp).toLocaleString() : 
                                'Unknown'
                            }
                          </span>
                        </div>
                        <button
                          className="mt-2 w-full py-2 border border-[#304a3f] text-[#304a3f] rounded-lg hover:bg-[#304a3f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => {
                            const lat = parseFloat(prompt('Enter latitude:', 
                              realtimeData[selectedVehicle._id || selectedVehicle.id]?.coordinates?.[1] || 
                              selectedVehicle.currentLocation?.coordinates?.[1] || 
                              '0'
                            ));
                            const lng = parseFloat(prompt('Enter longitude:', 
                              realtimeData[selectedVehicle._id || selectedVehicle.id]?.coordinates?.[0] || 
                              selectedVehicle.currentLocation?.coordinates?.[0] || 
                              '0'
                            ));
                            const address = prompt('Enter address (optional):', 
                              realtimeData[selectedVehicle._id || selectedVehicle.id]?.address || 
                              selectedVehicle.currentLocation?.address || 
                              ''
                            );
                            if (!isNaN(lat) && !isNaN(lng)) {
                              updateVehicleLocation(selectedVehicle._id || selectedVehicle.id, [lng, lat], address);
                            }
                          }}
                          disabled={updateLoading[selectedVehicle._id || selectedVehicle.id]}
                        >
                          {updateLoading[selectedVehicle._id || selectedVehicle.id] ? 'Updating...' : 'Update Location'}
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <MapPin size={32} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-500">No location data available</p>
                        <button
                          className="mt-2 py-2 px-4 border border-[#304a3f] text-[#304a3f] rounded-lg hover:bg-[#304a3f] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => {
                            const lat = parseFloat(prompt('Enter latitude:', '0'));
                            const lng = parseFloat(prompt('Enter longitude:', '0'));
                            const address = prompt('Enter address (optional):', '');
                            if (!isNaN(lat) && !isNaN(lng)) {
                              updateVehicleLocation(selectedVehicle._id || selectedVehicle.id, [lng, lat], address);
                            }
                          }}
                          disabled={updateLoading[selectedVehicle._id || selectedVehicle.id]}
                        >
                          {updateLoading[selectedVehicle._id || selectedVehicle.id] ? 'Updating...' : 'Add Location'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Driver Information */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-800">Driver Information</h3>
                      <button
                        onClick={() => {
                          setShowDetailsModal(false);
                          setSelectedVehicle(selectedVehicle);
                          fetchAvailableDrivers();
                          setShowAssignDriverModal(true);
                        }}
                        className="text-sm text-[#e28743] hover:text-[#d47834]"
                      >
                        {selectedVehicle.assignedDriver ? 'Change Driver' : 'Assign Driver'}
                      </button>
                    </div>
                    {selectedVehicle.assignedDriver ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#e28743] rounded-full flex items-center justify-center">
                            <User size={24} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{getDriverName(selectedVehicle.assignedDriver)}</p>
                            <p className="text-sm text-gray-600">
                              {selectedVehicle.assignedDriver.driverId || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500">Email</p>
                            <p className="font-medium">
                              {selectedVehicle.assignedDriver.user?.email || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Phone</p>
                            <p className="font-medium">
                              {selectedVehicle.assignedDriver.user?.phone || selectedVehicle.assignedDriver.user?.profile?.phone || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to unassign this driver?')) {
                              unassignDriverFromVehicle(selectedVehicle._id || selectedVehicle.id);
                              setShowDetailsModal(false);
                            }
                          }}
                          className="w-full py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={updateLoading[selectedVehicle._id || selectedVehicle.id]}
                        >
                          {updateLoading[selectedVehicle._id || selectedVehicle.id] ? 'Processing...' : 'Unassign Driver'}
                        </button>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <User size={32} className="mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-600">No driver assigned</p>
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            setSelectedVehicle(selectedVehicle);
                            fetchAvailableDrivers();
                            setShowAssignDriverModal(true);
                          }}
                          className="mt-2 text-[#e28743] hover:text-[#d47834]"
                        >
                          Assign a driver
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Specifications Card */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Specifications</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Engine Size</p>
                        <p className="font-medium">{selectedVehicle.specifications?.engineSize || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Fuel Type</p>
                        <p className="font-medium">{selectedVehicle.specifications?.fuelType || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Capacity</p>
                        <p className="font-medium">{selectedVehicle.specifications?.capacity || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Color</p>
                        <p className="font-medium">{selectedVehicle.specifications?.color || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Maintenance Card */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-semibold text-gray-800 mb-3">Maintenance</h3>
                    <div className="space-y-3">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-gray-600">Mileage</span>
                          <span className="font-medium">{(selectedVehicle.mileage || selectedVehicle.odometer || 0).toLocaleString()} km</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Last Service</p>
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{formatDate(selectedVehicle.lastService)}</span>
                          <span className={`px-2 py-1 rounded-full text-xs ${getServiceStatusColor(getServiceStatus(selectedVehicle.lastService))}`}>
                            {getServiceStatusText(getServiceStatus(selectedVehicle.lastService))}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Next Service</p>
                        <span className="font-medium">{formatDate(selectedVehicle.nextService)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {connected ? (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-green-600">Connected</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-sm text-red-600">Disconnected</span>
                    </>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    onClick={() => setShowDetailsModal(false)}
                  >
                    Close
                  </button>
                  <button
                    className={`px-4 py-2 rounded-lg ${
                      trackingVehicles[selectedVehicle._id || selectedVehicle.id] 
                        ? 'bg-red-600 text-white hover:bg-red-700' 
                        : 'bg-[#e28743] text-white hover:bg-[#d47834]'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    onClick={() => {
                      if (trackingVehicles[selectedVehicle._id || selectedVehicle.id]) {
                        stopVehicleTracking(selectedVehicle._id || selectedVehicle.id);
                      } else {
                        startVehicleTracking(selectedVehicle._id || selectedVehicle.id);
                      }
                    }}
                    disabled={updateLoading[selectedVehicle._id || selectedVehicle.id]}
                  >
                    {updateLoading[selectedVehicle._id || selectedVehicle.id] ? 'Processing...' : 
                     trackingVehicles[selectedVehicle._id || selectedVehicle.id] ? 'Stop Tracking' : 'Start Tracking'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredVehicles.length === 0 && !loading && (
        <div className="text-center py-12">
          <Truck className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vehicles found</h3>
          <p className="text-gray-600">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'No vehicles available in the system'}
          </p>
        </div>
      )}
    </div>
  );
};

export default Vehicles;